// src/redux/slices/productSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { productApi } from '../../api/productApi';

// ==================== ASYNC THUNKS ====================

// ✅ GET - Fetch all active products
export const fetchProductsAsync = createAsyncThunk(
  'products/fetchProducts',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔄 Fetching products from API...');
      const response = await productApi.getActiveProducts();
      console.log('✅ Products fetched successfully:', response);
      
      if (response?.status && response?.data) {
        return response;
      } else {
        return rejectWithValue('Invalid response format from server');
      }
    } catch (error) {
      console.error('❌ Fetch products error:', error);
      const errorMessage = typeof error === 'string' ? error : 'Failed to fetch products';
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ POST - Filter Products by Type
export const filterProductsAsync = createAsyncThunk(
  'products/filterProducts',
  async (filterData, { rejectWithValue }) => {
    try {
      console.log('🔄 POST - Filtering products with:', filterData);
      const response = await productApi.filterProducts(filterData);
      console.log('✅ POST - Filtered products:', response);
      
      if (response?.status && response?.data) {
        return response;
      } else {
        return rejectWithValue('Invalid response format from server');
      }
    } catch (error) {
      console.error('❌ POST - Filter products error:', error);
      const errorMessage = typeof error === 'string' ? error : 'Failed to filter products';
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ GET - Fetch product by ID
export const fetchProductByIdAsync = createAsyncThunk(
  'products/fetchProductById',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await productApi.getProductById(productId);
      return response;
    } catch (error) {
      console.error('❌ Fetch product error:', error);
      const errorMessage = typeof error === 'string' ? error : 'Failed to fetch product details';
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== INITIAL STATE ====================

const initialState = {
  products: [],
  filteredProducts: [],
  selectedProduct: null,
  isLoading: false,
  error: null,
  totalProducts: 0,
  vegProducts: [],
  nonVegProducts: [],
  lastFetched: null,
  activeFilter: null,
  isFiltering: false,
};

// ==================== SLICE ====================

const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedProduct: (state) => {
      state.selectedProduct = null;
    },
    // Local filter by type (without API call)
    filterByType: (state, action) => {
      const type = action.payload;
      state.activeFilter = type;
      if (type === 'Veg') {
        state.filteredProducts = state.products.filter(p => p.type === 'Veg');
      } else if (type === 'Non-Veg') {
        state.filteredProducts = state.products.filter(p => p.type === 'Non-Veg');
      } else {
        state.filteredProducts = state.products;
        state.activeFilter = null;
      }
    },
    // Search products locally
    searchProducts: (state, action) => {
      const query = action.payload.toLowerCase().trim();
      const sourceProducts = state.activeFilter 
        ? state.filteredProducts 
        : state.products;
        
      if (!query) {
        state.filteredProducts = sourceProducts;
        return;
      }
      
      state.filteredProducts = sourceProducts.filter(p => 
        p.product_name?.toLowerCase().includes(query) ||
        p.in_thali?.toLowerCase().includes(query) ||
        p.type?.toLowerCase().includes(query)
      );
    },
    // Sort products
    sortProducts: (state, action) => {
      const { field, order } = action.payload;
      const sorted = [...state.filteredProducts];
      sorted.sort((a, b) => {
        const valA = a[field] || '';
        const valB = b[field] || '';
        if (order === 'asc') {
          return valA > valB ? 1 : -1;
        } else {
          return valA < valB ? 1 : -1;
        }
      });
      state.filteredProducts = sorted;
    },
    // Reset filters
    resetFilters: (state) => {
      state.filteredProducts = state.products;
      state.activeFilter = null;
      state.isFiltering = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== FETCH ALL PRODUCTS ==========
      .addCase(fetchProductsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProductsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        const data = action.payload?.data || [];
        state.products = data;
        state.filteredProducts = data;
        state.totalProducts = data.length;
        state.vegProducts = data.filter(p => p.type === 'Veg');
        state.nonVegProducts = data.filter(p => p.type === 'Non-Veg');
        state.error = null;
        state.lastFetched = new Date().toISOString();
        state.activeFilter = null;
        console.log(`✅ Loaded ${data.length} products`);
      })
      .addCase(fetchProductsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string' 
          ? action.payload 
          : 'Failed to fetch products';
        state.products = [];
        state.filteredProducts = [];
        state.totalProducts = 0;
        console.error('❌ Product fetch rejected:', state.error);
      })
      
      // ========== FILTER PRODUCTS (POST API) ==========
      .addCase(filterProductsAsync.pending, (state) => {
        state.isLoading = true;
        state.isFiltering = true;
        state.error = null;
      })
      .addCase(filterProductsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isFiltering = false;
        const data = action.payload?.data || [];
        state.filteredProducts = data;
        state.totalProducts = data.length;
        state.activeFilter = action.meta.arg?.type || null;
        state.error = null;
        state.lastFetched = new Date().toISOString();
        console.log(`✅ Filtered ${data.length} products by type: ${state.activeFilter}`);
      })
      .addCase(filterProductsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.isFiltering = false;
        state.error = typeof action.payload === 'string' 
          ? action.payload 
          : 'Failed to filter products';
        console.error('❌ Product filter rejected:', state.error);
      })
      
      // ========== FETCH PRODUCT BY ID ==========
      .addCase(fetchProductByIdAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProductByIdAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedProduct = action.payload?.data || action.payload?.product || null;
        state.error = null;
      })
      .addCase(fetchProductByIdAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string' 
          ? action.payload 
          : 'Failed to fetch product details';
        state.selectedProduct = null;
      });
  },
});

// ==================== EXPORT ACTIONS & REDUCER ====================

export const {
  clearError,
  clearSelectedProduct,
  filterByType,
  searchProducts,
  sortProducts,
  resetFilters,
} = productSlice.actions;

export default productSlice.reducer;